import { useTranslation } from 'react-i18next';

import loginBg from '@/assets/branding/login-bg.png';
import logo from '@/assets/branding/idea-tsg-logo-light.png';

export function Login() {
  const { t } = useTranslation();

  // El SPA NO inicia el flujo OAuth: este enlace redirige al backend, que es
  // quien habla con Entra ID (patrón BFF). No se usa MSAL en el cliente.
  const loginUrl = '/api/auth/login';

  return (
    <div
      className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-3 py-6 sm:px-4 sm:py-8"
      style={{ backgroundColor: '#0f172a' }}
    >
      {/* Capa 1 — fondo difuminado a pantalla completa */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${loginBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center bottom',
          backgroundRepeat: 'no-repeat',
          filter: 'blur(50px)',
          zIndex: 0,
          opacity: 0.5,
          transform: 'scale(1.1)',
        }}
      />
      {/* Capa 2 — imagen principal nítida */}
      <div
        className="absolute inset-0 left-0 top-0 w-full"
        style={{
          backgroundImage: `url(${loginBg})`,
          backgroundSize: 'auto 100%',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          zIndex: 1,
          pointerEvents: 'none',
        }}
      />
      {/* Capa 3 — viñeta radial */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 15%, rgba(15, 23, 42, 0.7) 100%)',
          zIndex: 2,
        }}
      />
      {/* Capa 4 — degradado oscuro sutil */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-black/15 via-transparent to-black/40"
        style={{ zIndex: 3 }}
      />

      {/* Panel de login */}
      <div className="relative z-10 w-full max-w-sm space-y-5 sm:max-w-md sm:space-y-6">
        {/* Logo + tagline */}
        <div className="space-y-3 text-center sm:space-y-4">
          <div className="flex justify-center">
            <img
              src={logo}
              alt="IDEA TSG"
              className="h-10 object-contain sm:h-12 md:h-16"
            />
          </div>
          <p className="text-xs font-normal tracking-tight text-white/90 sm:text-sm">
            {t('login.tagline')}
          </p>
        </div>

        {/* Tarjeta glassmorphism */}
        <section
          aria-labelledby="login-title"
          className="space-y-5 rounded-xl border border-white/25 bg-white/15 p-6 shadow-2xl backdrop-blur-xl sm:space-y-6 sm:rounded-2xl sm:p-8"
        >
          <div className="space-y-3">
            <h2 id="login-title" className="text-xl font-bold text-gray-900">
              {t('login.title')}
            </h2>
            <p className="text-sm font-normal text-gray-700">{t('login.subtitle')}</p>
          </div>

          {/* Botón Microsoft — enlace BFF (sin MSAL en el cliente) */}
          <a
            href={loginUrl}
            className="group relative flex w-full items-center justify-center gap-2.5 overflow-hidden rounded-lg bg-gradient-to-b from-[#0078D4] to-[#0063B1] px-6 py-3.5 font-semibold text-white transition-all duration-300 hover:from-[#1084D7] hover:to-[#0066BB] hover:shadow-xl active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0078D4]/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
            {/* Logotipo Microsoft (4 cuadros) */}
            <span className="relative grid grid-cols-2 gap-0.5">
              <span className="h-3 w-3 rounded-[2px] bg-[#F25022]" />
              <span className="h-3 w-3 rounded-[2px] bg-[#7FBA00]" />
              <span className="h-3 w-3 rounded-[2px] bg-[#00A4EF]" />
              <span className="h-3 w-3 rounded-[2px] bg-[#FFB900]" />
            </span>
            <span className="relative text-base font-semibold">{t('login.cta')}</span>
          </a>

          {/* Separador */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/15" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white/10 px-3 font-medium text-gray-700">
                {t('login.or')}
              </span>
            </div>
          </div>

          {/* Caja de seguridad */}
          <div className="space-y-2 rounded-lg border border-blue-400/30 bg-gradient-to-r from-blue-500/15 to-cyan-500/15 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-900">
              {t('login.secureTitle')}
            </p>
            <p className="text-xs leading-relaxed text-gray-800">
              {t('login.secureBody')}
            </p>
          </div>

          <p className="text-center text-xs leading-relaxed text-gray-700">
            {t('login.legal')}
          </p>
        </section>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs font-medium text-white/70">{t('login.footer')}</p>
        </div>
      </div>
    </div>
  );
}
